import {
  Integer,
  ThemeIconKey,
  Box,
  Center,
  Hide,
  HStack,
  Icon,
  Show,
  Spinner,
  TextSize,
} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import {suffixTestId} from 'shared/web';

import {FlagLabel} from '../Flag/components/FlagLabel';
import {BaseFlagProps} from '../Flag/types';
import {getColorsFromScheme, getFlagElementSizes} from '../Flag/utils';
import {TagHoverBackgroundColor} from '../Tag/Tag';

interface DropdownControlProps extends BaseFlagProps {
  isOpen: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
}

export function DropdownControl(props: DropdownControlProps) {
  const theme = useTheme();
  const {height, labelHorizontalPadding, borderRadius} = getFlagElementSizes(props.size);
  const textSize: TextSize = props.size === 'small' ? 'xxSmall' : 'small';
  const iconSize: Integer = props.size === 'small' ? 3 : 4;
  const iconWrapperWidth = height;
  const {backgroundColor, color, hoverColor} = getColorsFromScheme(
    props.colorScheme,
    props.isSubtle
  );

  const iconPath: ThemeIconKey = props.isOpen ? 'navigation/expand_less' : 'navigation/expand_more';

  return (
    <div
      css={css`
        width: fit-content;
        border-radius: ${theme.radii[borderRadius]};

        &:focus-visible {
          outline: ${theme.outline.default} ${theme.colors.general.accent};
          outline-offset: ${theme.outline.offset};
        }
      `}
      role="button"
      aria-label="Toggle dropdown"
      aria-expanded={props.isOpen}
      tabIndex={0}
    >
      <Box
        borderRadius={borderRadius}
        height={height}
        backgroundColor={backgroundColor}
        overflow="hidden"
        position="relative"
        data-testid={suffixTestId('DropdownControl', props)}
      >
        <TagHoverBackgroundColor
          $hoverColorPath={props.isDisabled ? 'general.transparent' : hoverColor}
        >
          <HStack justify="space-between" align="center" height="100%">
            <Box
              paddingHorizontal={labelHorizontalPadding}
              data-testid={suffixTestId('DropdownControl-label', props)}
            >
              <FlagLabel label={props.label} size={textSize} color={color} />
            </Box>

            <Show when={props.isLoading}>
              <Spinner size="small" variant="overlay" />
            </Show>

            <Hide when={props.isDisabled}>
              <Center height="100%" width={iconWrapperWidth}>
                <Icon
                  value={iconPath}
                  size={iconSize}
                  color={color}
                  data-testid={suffixTestId('DropdownControl-icon', props)}
                />
              </Center>
            </Hide>
          </HStack>
        </TagHoverBackgroundColor>
      </Box>
    </div>
  );
}
